import React, { useState, useEffect, useRef, useContext } from 'react';
import * as XLSX from "xlsx";
import { useConfig } from '../utils/ConfigContext';
import { createAxiosInstance } from '../utils/AxiosInstance';
import { Tooltip, OverlayTrigger } from 'react-bootstrap';
import { end } from '@popperjs/core';
import { set, sub } from 'date-fns';
import FilterContext from "../utils/FilterContext";

// ✅ TABLE COLUMNS DEFINITION (keep keys aligned with transformed API data)
const MAIN_TABLE_HEADER_TOP = [
    { label: "#", rowSpan: 2 },
    { label: "Center Name", rowSpan: 2 },
    { label: "Doctor Name", rowSpan: 2 },
    { label: "Appointments", colSpan: 3 },
    { label: "Recommendations", colSpan: 3 },
    { label: "Conversion", colSpan: 4 },
    { label: "Amount", colSpan: 3 },
];

const MAIN_TABLE_HEADER_SECOND = [
    "OPD",
    "Patients",
    "Revenue",
    "SNB",
    "SDT",
    "SNB+SDT",
    "SNB",
    "SDT",
    "SNB+SDT",
    'Packages Amount',
    'Received Amount',
    'Treatments Amount'
];

const flatColumns = [
    { label: "#", key: "#" },
    { label: "Center Name", key: "Center Name" },
    { label: "Doctor Name", key: "doctor_name" },
    { label: "OPD", key: "total_appointments" },
    { label: "Patients", key: "unique_opd_patients" },
    { label: "Revenue", key: "appointment_revenue" },
    { label: "SNB", key: "recommended_snb" },
    { label: "SDT", key: "recommended_sdt" },
    { label: "SNB+SDT", key: "recommended_snb_sdt" },
    { label: "SNB", key: "convert_snb_count" },
    { label: "SDT", key: "convert_sdt_count" },
    { label: "SNB+SDT", key: "convert_snb_sdt_count" },
    { label: "Packages Amount", key: "convert_package_total_price" },
    { label: "Received Amount", key: "wallet_recieved_amount" },
    { label: "Treatments Amount", key: "treatment_debit_amount" },
];

// ✅ Transform one period block from API into the keys used by the table
// const transformPeriodToTable = (period = {}) => ({
//     total_appointments: period?.total_appointments ?? 0,
//     unique_opd_patients: period?.unique_opd_patients ?? 0,
//     appointment_revenue: period?.appointment_revenue ?? 0,
//     recommended_snb: period?.recommended_snb ?? 0,
//     recommended_sdt: period?.recommended_sdt ?? 0,
//     recommended_snb_sdt: period?.recommended_snb_sdt ?? 0,
//     booked_snb: period?.booked_snb ?? 0,
//     booked_sdt: period?.booked_sdt ?? 0,
//     booked_snb_sdt: period?.booked_snb_sdt ?? 0,
//     booked_price: period?.booked_price ?? 0,
//     package_revenue: period?.package_revenue ?? 0,
//     // old_booked_price: period?.old_booked_price ?? 0,
//     total_revenue: period?.total_revenue ?? 0,
// });



const stateOrderMap = {
    "Uttarakhand": 0,
    "West Bengal": 1,
    "Maharashtra": 2,
    "Karnataka": 3,
    "Tamil Nadu": 4,
    "Madhya Pradesh": 5,
    "New Delhi": 6,
};

const DoctorRevenue = () => {
    const [openPanels, setOpenPanels] = useState({});
    const [stateReports, setStateReports] = useState([]); // [{ stateName, centers: [...], summary: { last_day, last_month, this_month, last_year } }]
    const [isLoading, setIsLoading] = useState(false);
    const { API_BASE_URL } = useConfig(); // ✅ from context
    const AxiosInstance = createAxiosInstance(API_BASE_URL);
    const [openedStatePanel, setOpenedStatePanel] = useState(false);
    const reportTableRef = useRef(null);

    const [modalData, setModalData] = useState(null);
    const [modalLoading, setModalLoading] = useState(false);
    const [dataCategory, setDataCategory] = useState("");
    const [isFullScreenModal, setIsFullScreenModal] = useState(false);
    const [leadModel, setLeadModel] = useState(false);
    const [appointmentModel, setAppointmentModel] = useState(false);
    const [patientModel, setPatientModel] = useState(false);
    const [patientModalHeader, setPatientModalHeader] = useState("");
    const [pdfDropdownOpen, setPdfDropdownOpen] = useState(false);
    const [stateSelectionModal, setStateSelectionModal] = useState(false);
    const [selectedStatesForPrint, setSelectedStatesForPrint] = useState({});

    /* ----------------------------------------------------
       * FILTERS
       * -------------------------------------------------- */
    const { filters } = useContext(FilterContext);
    const filtersRef = useRef(filters);

    const currentDate = new Date();
    currentDate.setDate(currentDate.getDate() - 1);
    const previousDay = currentDate.toISOString().split("T")[0];

    const [rawStart, rawEnd] = filters?.dateRange?.split(" - ") ?? [];
    const startDate = rawStart?.trim() || previousDay;
    const endDate = rawEnd?.trim() || previousDay;


    const LeadStatusMap = {
        0: "New",
        1: "Scheduled",
        2: "Follow Up",
        3: "Converted",
        4: "Closed",
        5: "Transferred",
    };

    const firstHeaderRowStyle = {
        backgroundColor: "#009ed6",
        color: "white",
    };
    const firstHeaderRowSpanStyle = {

        backgroundColor: "#009ed6",
        color: "white",
    };
    const secondHeaderRowStyle = {

        backgroundColor: "#009ed6",
        color: "white",
    };

    // ✅ Toggle accordion panel open/close
    const togglePanel = (panelId) => {
        setOpenPanels((prev) => {
            const nextPanels = {
                ...prev,
                [panelId]: !prev[panelId],
            };

            const hasAnyOpenStatePanel = Object.keys(nextPanels).some(
                (key) => key.startsWith("state-") && nextPanels[key]
            );
            setOpenedStatePanel(hasAnyOpenStatePanel);

            return nextPanels;
        });
    };

    const buildEmptyMetrics = () => ({
        total_appointments: 0,
        unique_opd_patients: 0,
        appointment_revenue: 0,
        recommended_snb: 0,
        recommended_sdt: 0,
        recommended_snb_sdt: 0,
        convert_snb_count: 0,
        convert_sdt_count: 0,
        convert_snb_sdt_count: 0,
        convert_package_total_price: 0,
        wallet_recieved_amount: 0,
        treatment_debit_amount: 0,
        // total_revenue: 0,
    });


    const fetchStateData = async (centerIds, date) => {
        const response = await AxiosInstance.post('/doctor_revenue', { centerIds, startDate, endDate });
        const payload = response?.data?.data;
        if (Array.isArray(payload)) { return payload; }
        console.warn('Unexpected API response shape:', payload);
        return [];
    };

    const fetchCenters = async () => {
        try {
            setIsLoading(true);
            setStateReports([]);

            const response = await AxiosInstance.get('/centers/1');
            const centersData = response.data?.data || [];

            // Fetch doctor revenue data for all centers
            const centerIds = centersData
                .filter(c => c.hm_state?.trim() && c.hm_state?.trim() !== "New Delhi")
                .map(c => c.hm_id);

            if (centerIds.length === 0) {
                setIsLoading(false);
                return;
            }

            const doctorData = await fetchStateData(centerIds);

            // Group doctors by state, then by center
            const stateMap = {};

            doctorData.forEach((doctor) => {
                const state = doctor.hm_state?.trim();
                const centerId = doctor.hm_id;

                if (!state || state === "New Delhi") return;

                if (!stateMap[state]) {
                    stateMap[state] = {};
                }

                if (!stateMap[state][centerId]) {
                    stateMap[state][centerId] = {
                        hm_id: centerId,
                        hm_name: doctor.hm_name,
                        hm_state: state,
                        doctors: []
                    };
                }

                stateMap[state][centerId].doctors.push({
                    doctor_id: doctor.doctor_id,
                    doctor_name: doctor.doctor_name,
                    total_appointments: doctor.total_appointments,
                    unique_opd_patients: doctor.unique_opd_patients,
                    appointment_revenue: doctor.appointment_revenue,
                    recommended_snb: doctor.recommended_snb,
                    recommended_sdt: doctor.recommended_sdt,
                    recommended_snb_sdt: doctor.recommended_snb_sdt,
                    convert_snb_count: Number(doctor.convert_snb_count || 0),
                    convert_sdt_count: Number(doctor.convert_sdt_count || 0),
                    convert_snb_sdt_count: Number(doctor.convert_snb_sdt_count || 0),
                    convert_package_total_price: Number(doctor.convert_package_total_price || 0),
                    wallet_recieved_amount: Number(doctor.wallet_recieved_amount || 0),
                    treatment_debit_amount: parseFloat(parseFloat(doctor.treatment_debit_amount || 0).toFixed(2)),
                    // total_revenue: doctor.total_revenue,
                });
            });

            // Sort states and create reports
            const orderedStates = Object.keys(stateMap).sort((stateA, stateB) => {
                const orderA = stateOrderMap[stateA] ?? Number.MAX_SAFE_INTEGER;
                const orderB = stateOrderMap[stateB] ?? Number.MAX_SAFE_INTEGER;
                return orderA - orderB;
            });

            orderedStates.forEach((state) => {
                const centersInState = Object.values(stateMap[state]);

                // Build state-level summary by summing all doctors' metrics
                const summary = buildEmptyMetrics();
                centersInState.forEach((center) => {
                    center.doctors.forEach((doctor) => {
                        summary.total_appointments += doctor.total_appointments || 0;
                        summary.unique_opd_patients += doctor.unique_opd_patients || 0;
                        summary.appointment_revenue += doctor.appointment_revenue || 0;
                        summary.recommended_snb += doctor.recommended_snb || 0;
                        summary.recommended_sdt += doctor.recommended_sdt || 0;
                        summary.recommended_snb_sdt += doctor.recommended_snb_sdt || 0;
                        summary.convert_snb_count += doctor.convert_snb_count || 0;
                        summary.convert_sdt_count += doctor.convert_sdt_count || 0;
                        summary.convert_snb_sdt_count += doctor.convert_snb_sdt_count || 0;
                        summary.convert_package_total_price += doctor.convert_package_total_price || 0;
                        summary.wallet_recieved_amount += doctor.wallet_recieved_amount || 0;
                        summary.treatment_debit_amount += parseFloat((doctor.treatment_debit_amount || 0).toFixed(2));
                        // summary.old_booked_price += doctor.old_booked_price || 0;
                        // summary.total_revenue += doctor.total_revenue || 0;
                    });
                });

                const stateReport = {
                    stateName: state,
                    centers: centersInState,
                    summary: {
                        last_day: summary,
                    },
                };

                setStateReports((prev) => [...prev, stateReport]);
            });
        } catch (error) {
            console.error('Error fetching centers / doctor revenue:', error);
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        if (!filters) return;

        if (!filtersRef.current || filtersRef.current !== filters) {
            filtersRef.current = filters;
            fetchCenters();
        }
    }, [filters]);

    const periodConfigs = [
        { label: "Data", key: "last_day" },
    ];

    const buildExportRows = () => {
        const rows = [];
        const aplphabetLowercase = "abcdefghijklmnopqrstuvwxyz";

        stateReports.forEach((stateReport, stateIdx) => {
            // Add state summary row
            rows.push({
                __nameType: "state",
                __stateName: stateReport.stateName,
                "#": stateIdx + 1,
                "Center Name": stateReport.stateName,
                "doctor_name": "-",
                ...stateReport.summary.last_day,
            });

            // Add center and doctor rows
            stateReport.centers.forEach((center, centerIdx) => {
                // Calculate center summary
                const centerSummary = buildEmptyMetrics();
                center.doctors.forEach((doctor) => {
                    centerSummary.total_appointments += doctor.total_appointments || 0;
                    centerSummary.unique_opd_patients += doctor.unique_opd_patients || 0;
                    centerSummary.appointment_revenue += doctor.appointment_revenue || 0;
                    centerSummary.recommended_snb += doctor.recommended_snb || 0;
                    centerSummary.recommended_sdt += doctor.recommended_sdt || 0;
                    centerSummary.recommended_snb_sdt += doctor.recommended_snb_sdt || 0;
                    centerSummary.convert_snb_count += doctor.convert_snb_count || 0;
                    centerSummary.convert_sdt_count += doctor.convert_sdt_count || 0;
                    centerSummary.convert_snb_sdt_count += doctor.convert_snb_sdt_count || 0;
                    centerSummary.convert_package_total_price += doctor.convert_package_total_price || 0;
                    centerSummary.wallet_recieved_amount += doctor.wallet_recieved_amount || 0;
                    centerSummary.treatment_debit_amount += parseFloat((doctor.treatment_debit_amount || 0).toFixed(2));
                    // centerSummary.old_booked_price += doctor.old_booked_price || 0;
                    // centerSummary.total_revenue += doctor.total_revenue || 0;
                });

                // Add center row
                rows.push({
                    __nameType: "center",
                    __stateName: stateReport.stateName,
                    "#": stateIdx + 1 + "(" + aplphabetLowercase[centerIdx] + ")",
                    "Center Name": center.hm_name.replaceAll(/SAAOL /gi, "").replaceAll(stateReport.stateName, ""),
                    "doctor_name": "-",
                    ...centerSummary,
                });

                // Add doctor rows
                center.doctors.forEach((doctor) => {
                    rows.push({
                        __nameType: "doctor",
                        __stateName: stateReport.stateName,
                        "#": "-",
                        "Center Name": "",
                        "doctor_name": doctor.doctor_name,
                        total_appointments: doctor.total_appointments,
                        unique_opd_patients: doctor.unique_opd_patients,
                        appointment_revenue: doctor.appointment_revenue,
                        recommended_snb: doctor.recommended_snb,
                        recommended_sdt: doctor.recommended_sdt,
                        recommended_snb_sdt: doctor.recommended_snb_sdt,
                        convert_snb_count: doctor.convert_snb_count,
                        convert_sdt_count: doctor.convert_sdt_count,
                        convert_snb_sdt_count: doctor.convert_snb_sdt_count,
                        convert_package_total_price: doctor.convert_package_total_price,
                        wallet_recieved_amount: doctor.wallet_recieved_amount,
                        treatment_debit_amount: doctor.treatment_debit_amount,
                        // old_booked_price: doctor.old_booked_price,
                        // total_revenue: doctor.total_revenue,
                    });
                });
            });

            // Add blank row for state separation
            rows.push({
                __nameType: "blank",
                __stateName: stateReport.stateName,
                "#": "",
                "Center Name": "",
                "doctor_name": "",
            });
        });

        return rows;
    };

    const generateExcel = () => {
        if (!stateReports.length) {
            alert("No data available to export.");
            return;
        }

        const tableElement = reportTableRef.current;
        const sourceThead = tableElement?.querySelector("thead");
        const exportRows = buildExportRows();
        const dataRows = exportRows.map((row) =>
            flatColumns.map((col) => row[col.key] ?? "")
        );

        const fallbackHeaderRow = flatColumns.map((col) => col.label);
        let worksheet;

        if (sourceThead) {
            const topHeaderCells = Array.from(sourceThead.querySelectorAll("tr:first-child th"));
            const secondHeaderCells = Array.from(sourceThead.querySelectorAll("tr:nth-child(2) th"));
            const headerTopRow = [];
            const headerSecondRow = [];
            const merges = [];
            let secondRowIdx = 0;
            let colStart = 0;

            topHeaderCells.forEach((th) => {
                const label = (th.textContent || "").trim();
                const colSpan = Number(th.getAttribute("colspan") || 1);
                const rowSpan = Number(th.getAttribute("rowspan") || 1);

                headerTopRow.push(label);
                for (let i = 1; i < colSpan; i += 1) {
                    headerTopRow.push("");
                }

                if (rowSpan > 1) {
                    for (let i = 0; i < colSpan; i += 1) {
                        headerSecondRow.push("");
                    }
                    merges.push({
                        s: { r: 0, c: colStart },
                        e: { r: rowSpan - 1, c: colStart + colSpan - 1 },
                    });
                } else if (colSpan > 1) {
                    for (let i = 0; i < colSpan; i += 1) {
                        headerSecondRow.push((secondHeaderCells[secondRowIdx]?.textContent || "").trim());
                        secondRowIdx += 1;
                    }
                    merges.push({
                        s: { r: 0, c: colStart },
                        e: { r: 0, c: colStart + colSpan - 1 },
                    });
                } else {
                    headerSecondRow.push((secondHeaderCells[secondRowIdx]?.textContent || "").trim());
                    secondRowIdx += 1;
                }

                colStart += colSpan;
            });

            if (headerTopRow.length === flatColumns.length) {
                worksheet = XLSX.utils.aoa_to_sheet([headerTopRow, headerSecondRow, ...dataRows]);
                worksheet["!merges"] = merges;
            } else {
                worksheet = XLSX.utils.aoa_to_sheet([fallbackHeaderRow, ...dataRows]);
            }
        } else {
            worksheet = XLSX.utils.aoa_to_sheet([fallbackHeaderRow, ...dataRows]);
        }

        const workbook = XLSX.utils.book_new();
        const sheetName = "Center Daily Report";
        XLSX.utils.book_append_sheet(workbook, worksheet, sheetName);

        const today = new Date().toISOString().slice(0, 10);
        XLSX.writeFile(workbook, `center_daily_report_${today}.xlsx`);
    };

    const generatePdf = (filterStates = null, onlyStates = false) => {
        if (!stateReports.length) {
            alert("No data available to export.");
            return;
        }

        const tableElement = reportTableRef.current;
        const sourceThead = tableElement?.querySelector("thead");
        if (!sourceThead) {
            alert("Table not ready for PDF export.");
            return;
        }

        const escapeHtml = (text) => {
            if (text == null) return "";
            return String(text)
                .replace(/&/g, "&amp;")
                .replace(/</g, "&lt;")
                .replace(/>/g, "&gt;")
                .replace(/"/g, "&quot;")
                .replace(/'/g, "&#39;");
        };

        let exportRows = buildExportRows();

        // Filter to only show state rows if onlyStates is true
        if (onlyStates) {
            exportRows = exportRows.filter(row => {
                return row.__nameType === "state" || row.__nameType === "blank";
            });
        }

        // Filter rows by states if specified
        if (filterStates && filterStates.length > 0) {
            exportRows = exportRows.filter(row => {
                // Include rows that belong to selected states
                return filterStates.includes(row.__stateName);
            });
        }

        const headerRows = Array.from(sourceThead.querySelectorAll("tr"))
            .map((tr) => {
                const headerCells = Array.from(tr.querySelectorAll("th"))
                    .map((th) => {
                        const text = escapeHtml(th.textContent?.trim() || "");
                        const rowSpan = th.getAttribute("rowspan");
                        const colSpan = th.getAttribute("colspan");
                        const rowSpanAttr = rowSpan ? ` rowspan="${rowSpan}"` : "";
                        const colSpanAttr = colSpan ? ` colspan="${colSpan}"` : "";
                        return `<th${rowSpanAttr}${colSpanAttr}>${text}</th>`;
                    })
                    .join("");
                return `<tr>${headerCells}</tr>`;
            })
            .join("");
        const tableHeaderHtml = `<thead>${headerRows}</thead>`;

        const tbody = exportRows
            .map((row) => {
                const rowType = row.__nameType;

                // Handle blank separator rows
                if (rowType === "blank") {
                    return `<tr style="background-color: #f8f9fa; height: 10px;"><td colspan="16"></td></tr>`;
                }

                const rowCells = flatColumns
                    .map((col) => {
                        const cellValue = escapeHtml(row[col.key] ?? "");
                        if (col.key === "Center Name" && row[col.key]) {
                            const className =
                                rowType === "state" ? "print-state-name" : "print-center-name";
                            return `<td class="${className}">${cellValue}</td>`;
                        }
                        return `<td>${cellValue}</td>`;
                    })
                    .join("");
                return `<tr>${rowCells}</tr>`;
            })
            .join("");

        const printWindow = window.open("", "_blank");
        if (!printWindow) {
            alert("Please allow pop-ups to export PDF.");
            return;
        }

        printWindow.document.write(`
            <!DOCTYPE html>
            <html>
                <head>
                    <title>Center Daily Report</title>
                    <style>
                        body { font-family: Arial, sans-serif; padding: 16px; }
                        h2 { margin: 0 0 8px 0; }
                        .meta { margin: 0 0 12px 0; color: #444; font-size: 12px; }
                        table { border-collapse: collapse; width: 100%; font-size: 11px; white-space: nowrap; }
                        th, td { border: 1px solid #000; padding: 6px; text-align: center; }
                        th { background: #f4f4f4; }
                        thead { position: static !important; top: auto !important; }
                        td.print-state-name { font-weight: 900; text-align: left; }
                        td.print-center-name { font-weight: 600; text-align: right; }
                        @media print {
                            @page { size: landscape; margin: 10mm; }
                            tr:has(td.print-state-name) { page-break-before: always; }
                        }
                    </style>
                </head>
                <body>
                    <h2>Center Daily Report</h2>
                    <p class="meta">Generated on ${new Date().toLocaleString()}</p>
                    <table>
                        ${tableHeaderHtml}
                        <tbody>${tbody}</tbody>
                    </table>
                </body>
            </html>
        `);
        printWindow.document.close();
        printWindow.focus();

        setTimeout(() => {
            printWindow.print();
            printWindow.onafterprint = () => printWindow.close();
        }, 250);
    };



    const getDetails = async (row, column, subcolumn, centerIds, today = currentDate) => {
        try {

            if (!row || !column || !centerIds?.length) {
                console.warn("Missing parameters for fetching details", { row, column, centerIds });
                return;
            }

            const validrows = ["last_day", "last_month", "this_month", "last_year_this_month"];

            //validate timeline
            if (!validrows.includes(row)) {
                console.warn("Invalid timeline for fetching details", { row, column, centerIds });
                return;
            }

            const payload = {
                row,
                column,
                subcolumn,
                centerIds,
                today
            }
            setModalData([]);
            setModalLoading(true);
            setDataCategory(column);


            if (column === "leads") {
                setLeadModel(true);
                const response = await AxiosInstance.post('/centers/daily_report_details', payload);
                const data = response?.data?.data || [];

                const flatLeads = (data ?? []).flatMap(source =>
                    (subcolumn === 'total' ? source.total_leads : source.converted ?? []).map(lead => ({
                        ...lead,
                        source_name: source.source_name
                    }))
                );
                setModalData(flatLeads);
            } else if (column === "appointments") {
                setAppointmentModel(true);
                const response = await AxiosInstance.post('/centers/daily_report_details', payload);
                const data = response?.data?.data || [];
                setModalData(data);
            } else if (column === 'package_booked' || column === 'package_completed' || column === 'treatment_scheduled' || column === 'treatment_completed') {
                setPatientModel(true);
                setPatientModalHeader(column.replaceAll("_", " ").toUpperCase() + " - " + subcolumn.toUpperCase());
                const response = await AxiosInstance.post('/centers/daily_report_details', payload);
                const data = response?.data?.data || [];
                setModalData(data);
            }

        } catch (error) {
            console.error("Error in getDetails parameters", { row, column, subcolumn, centerIds, today }, error);
        } finally {
            setModalLoading(false);
        }

    }
    const toggleFullScreenModal = () => {
        setIsFullScreenModal(prev => !prev);
    };




    const escapeHtml = (text) => {
        if (text == null) return "";
        const div = document.createElement("div");
        div.textContent = text;
        return div.innerHTML;
    };
    const getAge = (dob) => {
        if (!dob) return 0;

        const birthDate = new Date(dob);
        const today = new Date();

        let age = today.getFullYear() - birthDate.getFullYear();

        const m = today.getMonth() - birthDate.getMonth();
        if (m < 0 || (m === 0 && today.getDate() < birthDate.getDate())) {
            age--;
        }


        return age + ' Years';

    };
    const formatedDate = (dateString) => {
        if (!dateString) return "";
        const date = new Date(dateString);
        const day = String(date.getDate()).padStart(2, "0");
        const month = String(date.getMonth() + 1).padStart(2, "0");
        const year = date.getFullYear();
        return `${day}-${month}-${year}`;
    };
    const DetailItem = ({ type, sessions, status, dateLabel, date, last_treatment_date = null }) => (
        <div
            className="border rounded px-2 py-1 mb-1 bg-white"
        >
            <span className="fw-semibold">{type}-{sessions} ({status})</span><br />
            <span className="text-muted small">{dateLabel}: {date}</span>
            {last_treatment_date && (
                <>
                    <br />
                    <span className="text-muted small">Last Treatment: {last_treatment_date}</span>
                </>
            )}
        </div>
    );
    const TYPE_MAP = {
        1: "SDT",
        2: "SNB",
    };

    const PackageStatusMap = {
        0: 'Not Started',
        1: 'Activated',
        2: 'Completed',
        3: "Cancelled",
        4: "Cancelling"
    }

    const TABLE_CONFIG = {
        package: {
            label: "Package Details",
            key: "package_details",
            typeLabel: "Package Type",
            dateLabel: "Booking Date",
            getType: (item) => TYPE_MAP[item.package_type],
            getDate: (item) => formatedDate(item.tppm_date),
            getLastTreatmentDate: (item) => item.last_treatment_date ? formatedDate(item.last_treatment_date) : '',
            getStatus: (item) => PackageStatusMap[item.status],
            getSessions: (item) => item.sessions,
        },
        treatment: {
            label: "Treatment Details",
            key: "treatment_details",
            typeLabel: "Treatment Type",
            dateLabel: "Treatment Date",
            getType: (item) => TYPE_MAP[item.treatment_type || item.package_type],
            getDate: (item) => formatedDate(item.ttm_date || item.slot_date),
            getLastTreatmentDate: (item) => item.last_treatment_date ?? '',
            getStatus: (item) => PackageStatusMap[item.package_status || item.tppm_status],
            getSessions: (item) => item.total_sessions || item.sessions,
        },
    };

    const mode =
        dataCategory.startsWith('treatment') ? "treatment" : "package";

    const config = TABLE_CONFIG[mode];

    const [expandedRows, setExpandedRows] = useState({});

    const toggleRow = (pm_id) => {
        setExpandedRows(prev => ({
            ...prev,
            [pm_id]: !prev[pm_id]
        }));
    };
    const secondsToHMS = (totalSeconds) => {
        totalSeconds = Number(totalSeconds);

        const hours = Math.floor(totalSeconds / 3600);
        const minutes = Math.floor((totalSeconds % 3600) / 60);
        const seconds = totalSeconds % 60;

        let result = "";

        if (hours > 0) result += `${hours}h `;
        if (minutes > 0) result += `${minutes}m `;
        if (seconds > 0 || result === "") result += `${seconds}s`;

        return result.trim();

    }

    return (
        <>
            <div className="DailyReportTableTest pb-80">
                <div >
                    {isLoading ? (
                        <div className="text-center py-2">
                            Loading daily report state wise...
                        </div>
                    ) : (
                        <div className="btn-group d-flex justify-content-end position-relative">
                            <OverlayTrigger
                                placement="top"
                                delay={{ show: 250, hide: 400 }}
                                overlay={
                                    <Tooltip id="button-pdf">
                                        Print Pdf
                                    </Tooltip>
                                }
                            >
                                <div className=" cursor-pointer w-40-px h-40-px bg-neutral-200 rounded-circle d-flex justify-content-center align-items-center me-2 position-relative"
                                    onClick={() => setPdfDropdownOpen(!pdfDropdownOpen)} >
                                    <i className="ri-file-pdf-2-line"></i>

                                    {pdfDropdownOpen && (
                                        <div style={{
                                            position: 'absolute',
                                            top: '100%',
                                            right: 0,
                                            backgroundColor: 'white',
                                            border: '1px solid #ccc',
                                            borderRadius: '4px',
                                            boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                                            zIndex: 1000,
                                            minWidth: '150px',
                                            marginTop: '5px'
                                        }}>
                                            <div
                                                style={{
                                                    padding: '10px 15px',
                                                    cursor: 'pointer',
                                                    borderBottom: '1px solid #eee',
                                                    fontSize: '12px'
                                                }}
                                                onMouseEnter={(e) => e.target.style.backgroundColor = '#f5f5f5'}
                                                onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
                                                onClick={() => {
                                                    generatePdf();
                                                    setPdfDropdownOpen(false);
                                                }}
                                            >
                                                Print Full Table
                                            </div>
                                            <div
                                                style={{
                                                    padding: '10px 15px',
                                                    cursor: 'pointer',
                                                    fontSize: '12px'
                                                }}
                                                onMouseEnter={(e) => e.target.style.backgroundColor = '#f5f5f5'}
                                                onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
                                                onClick={() => {
                                                    setStateSelectionModal(true);
                                                    setPdfDropdownOpen(false);
                                                    // Reset selections
                                                    setSelectedStatesForPrint({});
                                                }}
                                            >
                                                Print Full States
                                            </div>
                                            <div
                                                style={{
                                                    padding: '10px 15px',
                                                    cursor: 'pointer',
                                                    fontSize: '12px'
                                                }}
                                                onMouseEnter={(e) => e.target.style.backgroundColor = '#f5f5f5'}
                                                onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
                                                onClick={() => {
                                                    generatePdf(null, true);
                                                    setPdfDropdownOpen(false);
                                                }}
                                            >
                                                Print Only States
                                            </div>
                                        </div>
                                    )}

                                </div>
                            </OverlayTrigger>
                            <OverlayTrigger
                                placement="top"
                                delay={{ show: 250, hide: 400 }}
                                overlay={
                                    <Tooltip id="button-excel">
                                        Download Excel
                                    </Tooltip>
                                }
                            >


                                <div className=" cursor-pointer w-40-px h-40-px bg-neutral-200 rounded-circle d-flex justify-content-center align-items-center me-2"
                                    onClick={generateExcel} >
                                    <i className="ri-file-excel-2-line"></i>

                                </div>

                            </OverlayTrigger>

                        </div>

                    )}
                    <div className='' style={{ minHeight: "400px", maxHeight: "800px", overflowX: "auto", WebkitOverflowScrolling: "touch" }}>
                        <table
                            ref={reportTableRef}
                            className="table table-bordered border-2 table-hover text-center align-middle mb-0"
                            style={{ whiteSpace: "nowrap" }}
                        >

                            {/* ================= HEADER ================= */}
                            <thead className="table-primary text-light text-white text-center align-middle border-3 " style={{ position: "sticky", top: 0, zIndex: 3 }}>

                                {/* FIRST ROW */}
                                <tr>
                                    {MAIN_TABLE_HEADER_TOP.map((header, index) => (
                                        <th
                                            key={`header-top-${index}`}
                                            rowSpan={header.rowSpan}
                                            colSpan={header.colSpan}
                                            style={header.rowSpan ? firstHeaderRowSpanStyle : firstHeaderRowStyle}
                                        >
                                            {header.label}
                                        </th>
                                    ))}
                                </tr>

                                {/* SECOND ROW */}
                                <tr>
                                    {MAIN_TABLE_HEADER_SECOND.map((label, index) => (
                                        <th key={`header-second-${index}`} style={secondHeaderRowStyle}>
                                            {label}
                                        </th>
                                    ))}
                                </tr>
                            </thead>

                            {/* ================= BODY ================= */}
                            <tbody>
                                {stateReports.length ? (

                                    stateReports.map((stateReport, stateIdx) => (
                                        <React.Fragment key={stateReport.stateName}>
                                            {/* STATE ROW */}
                                            <tr
                                                key={`${stateReport.stateName}-summary`}
                                                className="table-primary fw-bold"
                                                style={{ cursor: "pointer" }}
                                            >
                                                <td>{stateIdx + 1}</td>
                                                <td onClick={() => togglePanel(`state-${stateReport.stateName}`)}>{stateReport.stateName}</td>
                                                <td>-</td>
                                                <td>{stateReport.summary.last_day.total_appointments ?? 0}</td>
                                                <td>{stateReport.summary.last_day.unique_opd_patients ?? 0}</td>
                                                <td>{stateReport.summary.last_day.appointment_revenue ?? 0}</td>
                                                <td>{stateReport.summary.last_day.recommended_snb ?? 0}</td>
                                                <td>{stateReport.summary.last_day.recommended_sdt ?? 0}</td>
                                                <td>{stateReport.summary.last_day.recommended_snb_sdt ?? 0}</td>
                                                <td>{stateReport.summary.last_day.convert_snb_count ?? 0}</td>
                                                <td>{stateReport.summary.last_day.convert_sdt_count ?? 0}</td>
                                                <td>{stateReport.summary.last_day.convert_snb_sdt_count ?? 0}</td>
                                                <td>{stateReport.summary.last_day.convert_package_total_price ?? 0}</td>
                                                <td>{stateReport.summary.last_day.wallet_recieved_amount ?? 0}</td>
                                                <td>{((stateReport.summary.last_day.treatment_debit_amount ?? 0).toFixed(2))}</td>
                                            </tr>

                                            {/* CENTER AND DOCTOR ROWS */}
                                            {!!openPanels[`state-${stateReport.stateName}`] && stateReport.centers.map((center, centerIdx) => {
                                                const centerPanelId = `center-${stateReport.stateName}-${center.hm_id}`;
                                                const isCenterOpen = !!openPanels[centerPanelId];
                                                const aplphabetLowercase = "abcdefghijklmnopqrstuvwxyz";

                                                // Calculate center-level summary
                                                const centerSummary = buildEmptyMetrics();
                                                center.doctors.forEach((doctor) => {
                                                    centerSummary.total_appointments += doctor.total_appointments || 0;
                                                    centerSummary.unique_opd_patients += doctor.unique_opd_patients || 0;
                                                    centerSummary.appointment_revenue += doctor.appointment_revenue || 0;
                                                    centerSummary.recommended_snb += doctor.recommended_snb || 0;
                                                    centerSummary.recommended_sdt += doctor.recommended_sdt || 0;
                                                    centerSummary.recommended_snb_sdt += doctor.recommended_snb_sdt || 0;
                                                    centerSummary.convert_snb_count += doctor.convert_snb_count || 0;
                                                    centerSummary.convert_sdt_count += doctor.convert_sdt_count || 0;
                                                    centerSummary.convert_snb_sdt_count += doctor.convert_snb_sdt_count || 0;
                                                    centerSummary.convert_package_total_price += doctor.convert_package_total_price || 0;
                                                    centerSummary.wallet_recieved_amount += doctor.wallet_recieved_amount || 0;
                                                    centerSummary.treatment_debit_amount += parseFloat((doctor.treatment_debit_amount || 0).toFixed(2));
                                                    // centerSummary.old_booked_price += doctor.old_booked_price || 0;
                                                    // centerSummary.total_revenue += doctor.total_revenue || 0;
                                                });

                                                return (
                                                    <React.Fragment key={centerPanelId}>
                                                        {/* CENTER ROW */}
                                                        <tr
                                                            style={{ cursor: "pointer" }}
                                                            className="table-info fw-bold"
                                                        >
                                                            <td>{stateIdx + 1 + "(" + (aplphabetLowercase[centerIdx] ?? "") + ")"}</td>
                                                            <td onClick={() => togglePanel(centerPanelId)}>{center.hm_name.replaceAll(/SAAOL /gi, '').replaceAll(stateReport.stateName, "")} ({center.doctors.length})</td>
                                                            <td>-</td>
                                                            <td>{centerSummary.total_appointments}</td>
                                                            <td>{centerSummary.unique_opd_patients}</td>
                                                            <td>{centerSummary.appointment_revenue}</td>
                                                            <td>{centerSummary.recommended_snb}</td>
                                                            <td>{centerSummary.recommended_sdt}</td>
                                                            <td>{centerSummary.recommended_snb_sdt}</td>
                                                            <td>{centerSummary.convert_snb_count}</td>
                                                            <td>{centerSummary.convert_sdt_count}</td>
                                                            <td>{centerSummary.convert_snb_sdt_count}</td>
                                                            <td>{centerSummary.convert_package_total_price}</td>
                                                            <td>{centerSummary.wallet_recieved_amount}</td>
                                                            <td>{((centerSummary.treatment_debit_amount ?? 0).toFixed(2))}</td>
                                                        </tr>

                                                        {/* DOCTOR ROWS */}
                                                        {isCenterOpen && center.doctors.map((doctor, doctorIdx) => (
                                                            <tr key={`${centerPanelId}-doctor-${doctor.doctor_id}`} style={{ fontSize: "0.9em" }}>
                                                                <td>-</td>
                                                                <td></td>
                                                                <td>{doctor.doctor_name}</td>
                                                                <td>{doctor.total_appointments ?? 0}</td>
                                                                <td>{doctor.unique_opd_patients ?? 0}</td>
                                                                <td>{doctor.appointment_revenue ?? 0}</td>
                                                                <td>{doctor.recommended_snb ?? 0}</td>
                                                                <td>{doctor.recommended_sdt ?? 0}</td>
                                                                <td>{doctor.recommended_snb_sdt ?? 0}</td>
                                                                <td>{doctor.convert_snb_count ?? 0}</td>
                                                                <td>{doctor.convert_sdt_count ?? 0}</td>
                                                                <td>{doctor.convert_snb_sdt_count ?? 0}</td>
                                                                <td>{doctor.convert_package_total_price ?? 0}</td>
                                                                <td>{doctor.wallet_recieved_amount ?? 0}</td>
                                                                <td>{((doctor.treatment_debit_amount ?? 0).toFixed(2))}</td>
                                                                {/* <td>{doctor.old_booked_price ?? 0}</td> */}
                                                                {/* <td>{doctor.total_revenue ?? 0}</td> */}
                                                            </tr>
                                                        ))}
                                                    </React.Fragment>
                                                );
                                            })}

                                            {/* Blank row for state separation */}
                                            <tr style={{ height: "10px", backgroundColor: "#f8f9fa" }}>
                                                <td colSpan="16"></td>
                                            </tr>
                                        </React.Fragment>

                                    ))

                                ) : (
                                    <tr>
                                        <td colSpan="16" className="text-center">
                                            No Data Available
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>

                    </div>




                </div>
            </div>
            {
                leadModel && (
                    <>
                        {/* Backdrop */}
                        <div className="modal-backdrop fade show w-100 h-100"></div>

                        <div
                            className="modal d-block"
                            tabIndex="-1"
                            style={{ position: "fixed", overflowY: "auto" }}
                        >
                            <div className={`modal-dialog ${isFullScreenModal ? 'modal-fullscreen w-100' : 'modal-xl'} modal-dialog-centered modal-dialog-scrollable`}>
                                <div className="modal-content">

                                    {/* Header */}
                                    <div className="modal-header d-flex justify-content-between align-items-center">
                                        <h5 className="modal-title">Lead Details</h5>
                                        <div className="d-flex align-items-center">
                                            {/* download csv button */}
                                            {/* <div className=" cursor-pointer w-40-px h-40-px bg-neutral-200 rounded-circle d-flex justify-content-center align-items-center me-2"
                                            >
                                                <i className="ri-file-pdf-2-line"></i>

                                            </div>
                                            <div className=" cursor-pointer w-40-px h-40-px bg-neutral-200 rounded-circle d-flex justify-content-center align-items-center me-2"
                                            >
                                                <i className="ri-file-excel-2-line"></i>

                                            </div> */}

                                            <div className='cursor-pointer w-40-px h-40-px bg-neutral-200 rounded-circle d-flex justify-content-center align-items-center'
                                                onClick={toggleFullScreenModal} >
                                                {isFullScreenModal ? <i className="ri-fullscreen-exit-line"></i> : <i className="ri-fullscreen-line"></i>}

                                            </div>

                                            <button
                                                type="button"
                                                className="btn-close"
                                                onClick={() => setLeadModel(false)}
                                            ></button>

                                        </div>

                                    </div>

                                    {/* Body */}
                                    <div className="modal-body">
                                        {/* center wise counting */}


                                        {/* Loader */}
                                        {modalLoading && (
                                            <div className="p-4 text-center">
                                                <div className="spinner-border text-primary"></div>
                                                <p className="mt-2 mb-0 fw-bold">Loading...</p>
                                            </div>
                                        )}

                                        {/* Table */}
                                        {!modalLoading && modalData.length > 0 && (
                                            <table className="table table-bordered">
                                                <thead
                                                    style={{
                                                        position: "sticky",
                                                        top: -18,
                                                        backgroundColor: "white",
                                                        zIndex: 1,
                                                    }}
                                                >
                                                    <tr>
                                                        <th>#</th>
                                                        <th>Lead Name</th>
                                                        <th>Phone Number</th>
                                                        <th>Age</th>
                                                        <th>Center Name</th>
                                                        <th>Primary Source</th>
                                                        <th>Status</th>
                                                    </tr>
                                                </thead>

                                                <tbody>
                                                    {modalData.map((lead, index) => (
                                                        <tr key={lead.lead_id}>
                                                            <td>{index + 1}</td>
                                                            <td>{lead.name}</td>
                                                            <td>{lead.phone}</td>
                                                            <td> {lead.dob ? getAge(lead.dob) : '-'} </td>
                                                            <td>{lead.center_name}</td>
                                                            <td>
                                                                {lead.source_name === "Transfer from Other Center"
                                                                    ? `Transferred From ${lead.transferred_from}`
                                                                    : lead.source_name}
                                                            </td>
                                                            <td>
                                                                {LeadStatusMap[lead.status] || "Unknown"}
                                                            </td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        )}

                                        {/* Empty State */}
                                        {!modalLoading && modalData.length === 0 && (
                                            <p className="text-center fw-bold">No leads found</p>
                                        )}

                                    </div>
                                </div>
                            </div>
                        </div>
                    </>
                )
            }
            {
                appointmentModel && (
                    <>
                        {/* Backdrop */}
                        <div className="modal-backdrop fade show w-100 h-100"></div>

                        <div
                            className="modal d-block"
                            tabIndex="-1"
                            style={{ position: "fixed", overflowY: "auto" }}
                        >
                            <div className={`modal-dialog ${isFullScreenModal ? 'modal-fullscreen w-100' : 'modal-xl'} modal-dialog-centered modal-dialog-scrollable`}>
                                <div className="modal-content">

                                    {/* Header */}
                                    <div className="modal-header d-flex justify-content-between align-items-center">
                                        <h5 className="modal-title">Appointments Details</h5>
                                        <div className="d-flex align-items-center">
                                            {/* <div className=" cursor-pointer w-40-px h-40-px bg-neutral-200 rounded-circle d-flex justify-content-center align-items-center me-2"
                                            >
                                                <i className="ri-file-pdf-2-line"></i>

                                            </div> */}

                                            {/* <div className="cursor-pointer w-40-px h-40-px bg-neutral-200 rounded-circle d-flex justify-content-center align-items-center me-2"
                                            >
                                                <i className="ri-file-excel-2-line"></i>

                                            </div> */}

                                            <div className='cursor-pointer w-40-px h-40-px bg-neutral-200 rounded-circle d-flex justify-content-center align-items-center'
                                                onClick={toggleFullScreenModal} >
                                                {isFullScreenModal ? <i className="ri-fullscreen-exit-line"></i> : <i className="ri-fullscreen-line"></i>}

                                            </div>
                                            <button
                                                type="button"
                                                className="btn-close"
                                                onClick={() => setAppointmentModel(false)}
                                            ></button>
                                        </div>
                                    </div>

                                    {/* Body */}
                                    <div className="modal-body">


                                        {/* Loader */}
                                        {modalLoading && (
                                            <div className="p-4 text-center">
                                                <div className="spinner-border text-primary"></div>
                                                <p className="mt-2 mb-0 fw-bold">Loading...</p>
                                            </div>
                                        )}

                                        {/* Table */}
                                        {!modalLoading && modalData.length > 0 && (
                                            <table className="table table-bordered">
                                                <thead
                                                    style={{
                                                        position: "sticky",
                                                        top: -18,
                                                        backgroundColor: "white",
                                                        zIndex: 1,
                                                    }}
                                                >
                                                    <tr>
                                                        <th>#</th>
                                                        <th>Patient Name</th>
                                                        <th>Patient ID</th>
                                                        <th>Age</th>
                                                        <th>Center Name</th>
                                                        <th>Dr. Name</th>
                                                        <th>Date</th>
                                                    </tr>
                                                </thead>

                                                <tbody>
                                                    {modalData.map((appointment, index) => (
                                                        <tr key={appointment.pam_id}>
                                                            <td>{index + 1}</td>
                                                            <td>{appointment.pm_name}</td>
                                                            <td>{appointment.patient_unique_id}</td>
                                                            <td>{appointment.pm_dob ? getAge(appointment.pm_dob) : '-'}</td>
                                                            <td>{appointment.hm_name}</td>
                                                            <td>
                                                                {appointment.doctor_name || "N/A"}
                                                            </td>
                                                            <td>
                                                                {appointment.pam_app_date || "Unknown"}
                                                            </td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        )}

                                        {/* Empty State */}
                                        {!modalLoading && modalData.length === 0 && (
                                            <p className="text-center fw-bold">No Appointments found</p>
                                        )}

                                    </div>
                                </div>
                            </div>
                        </div>
                    </>
                )
            }
            {
                patientModel && (
                    <>
                        {/* Backdrop */}
                        <div className="modal-backdrop fade show w-100 h-100"></div>

                        <div
                            className="modal d-block"
                            tabIndex="-1"
                            style={{ position: "fixed", overflowY: "auto" }}
                        >
                            <div className={`modal-dialog ${isFullScreenModal ? 'modal-fullscreen w-100' : 'modal-xl'} modal-dialog-centered modal-dialog-scrollable`}>
                                <div className="modal-content">

                                    {/* Header */}
                                    <div className="modal-header d-flex justify-content-between align-items-center">
                                        <h5 className="modal-title">{patientModalHeader}</h5>
                                        <div className="d-flex align-items-center">
                                            {/* <div className=" cursor-pointer w-40-px h-40-px bg-neutral-200 rounded-circle d-flex justify-content-center align-items-center me-2"
                                            >
                                                <i className="ri-file-pdf-2-line"></i>

                                            </div>
                                           
                                            <div className="cursor-pointer w-40-px h-40-px bg-neutral-200 rounded-circle d-flex justify-content-center align-items-center me-2"
                                            >
                                                <i className="ri-file-excel-2-line"></i>

                                            </div> */}

                                            <div className='cursor-pointer w-40-px h-40-px bg-neutral-200 rounded-circle d-flex justify-content-center align-items-center'
                                                onClick={toggleFullScreenModal} >
                                                {isFullScreenModal ? <i className="ri-fullscreen-exit-line"></i> : <i className="ri-fullscreen-line"></i>}

                                            </div>
                                            <button
                                                type="button"
                                                className="btn-close"
                                                onClick={() => setPatientModel(false)}
                                            ></button>
                                        </div>
                                    </div>

                                    {/* Body */}
                                    <div className="modal-body">
                                        {/* Loader */}
                                        {modalLoading && (
                                            <div className="p-4 text-center">
                                                <div className="spinner-border text-primary"></div>
                                                <p className="mt-2 mb-0 fw-bold">Loading...</p>
                                            </div>
                                        )}

                                        {/* Table */}
                                        {!modalLoading && modalData.length > 0 && (
                                            <table className="table table-bordered">
                                                <thead
                                                    style={{
                                                        position: "sticky",
                                                        top: -18,
                                                        backgroundColor: "white",
                                                        zIndex: 1,
                                                    }}
                                                >
                                                    <tr>
                                                        <th>#</th>
                                                        <th>Patient Name</th>
                                                        <th>PUID</th>
                                                        <th>Age</th>
                                                        <th>Phone</th>
                                                        <th>Center Name</th>
                                                        <th colSpan={2}>{config.label}</th>
                                                    </tr>
                                                </thead>

                                                <tbody>
                                                    {modalData.map((patient, index) => {
                                                        const items = patient[config.key] || [];
                                                        const visibleItems = items.slice(0, 2);
                                                        const isExpanded = expandedRows[patient.pm_id];

                                                        return (
                                                            <React.Fragment key={patient.pm_id}>
                                                                {/* MAIN ROW */}
                                                                <tr>
                                                                    <td>{index + 1}</td>
                                                                    <td>{patient.patient_name}</td>
                                                                    <td>{patient.patient_unique_id ?? "N/A"}</td>
                                                                    <td>{patient.pm_dob ? getAge(patient.pm_dob) : '-'}</td>
                                                                    <td>{patient.phone ?? "N/A"}</td>
                                                                    <td>{patient.center_name}</td>

                                                                    <td colSpan={2}>
                                                                        {visibleItems.map((item, i) => (
                                                                            <DetailItem
                                                                                key={i}
                                                                                type={config.getType(item)}
                                                                                sessions={config.getSessions(item)}
                                                                                status={config.getStatus(item)}
                                                                                dateLabel={config.dateLabel}
                                                                                date={config.getDate(item)}
                                                                                last_treatment_date={config.getLastTreatmentDate(item)}
                                                                            />
                                                                        ))}

                                                                        {items.length > 2 && (
                                                                            <div
                                                                                className="text-primary small fw-semibold cursor-pointer mt-1"
                                                                                onClick={() => toggleRow(patient.pm_id)}
                                                                            >
                                                                                {isExpanded
                                                                                    ? "▲ Hide details"
                                                                                    : `▼ View all (${items.length})`}
                                                                            </div>
                                                                        )}
                                                                    </td>
                                                                </tr>

                                                                {/* COLLAPSED ROW */}
                                                                {isExpanded && (
                                                                    <tr className="bg-light">
                                                                        <td></td>
                                                                        <td></td>
                                                                        <td></td>
                                                                        <td></td>
                                                                        <td></td>
                                                                        <td colSpan={2}>
                                                                            <div style={{ maxHeight: 200, overflowY: "auto" }}>
                                                                                {items.map((item, i) => (
                                                                                    <DetailItem
                                                                                        key={i}
                                                                                        type={config.getType(item)}
                                                                                        sessions={config.getSessions(item)}
                                                                                        status={config.getStatus(item)}
                                                                                        dateLabel={config.dateLabel}
                                                                                        date={config.getDate(item)}
                                                                                        last_treatment_date={config.getLastTreatmentDate(item)}
                                                                                    />
                                                                                ))}
                                                                            </div>
                                                                        </td>
                                                                    </tr>
                                                                )}
                                                            </React.Fragment>
                                                        );
                                                    })}

                                                </tbody>
                                            </table>
                                        )}

                                        {/* Empty State */}
                                        {!modalLoading && modalData.length === 0 && (
                                            <p className="text-center fw-bold">No Details found</p>
                                        )}

                                    </div>
                                </div>
                            </div>
                        </div>
                    </>
                )
            }

            {/* State Selection Modal for PDF */}
            {stateSelectionModal && (
                <>
                    <div className="modal-backdrop fade show w-100 h-100"></div>
                    <div
                        className="modal d-block"
                        tabIndex="-1"
                        style={{ position: "fixed", overflowY: "auto" }}
                    >
                        <div className="modal-dialog modal-dialog-centered">
                            <div className="modal-content">
                                <div className="modal-header">
                                    <h5 className="modal-title">Select States to Print</h5>
                                    <button
                                        type="button"
                                        className="btn-close"
                                        onClick={() => setStateSelectionModal(false)}
                                    ></button>
                                </div>
                                <div className="modal-body">
                                    <div style={{ maxHeight: "400px", overflowY: "auto" }}>
                                        {stateReports.map((stateReport) => (
                                            <div key={stateReport.stateName} className="form-check mb-3">
                                                <input
                                                    type="checkbox"
                                                    className="form-check-input"
                                                    id={`state-${stateReport.stateName}`}
                                                    checked={selectedStatesForPrint[stateReport.stateName] || false}
                                                    onChange={(e) => {
                                                        setSelectedStatesForPrint(prev => ({
                                                            ...prev,
                                                            [stateReport.stateName]: e.target.checked
                                                        }));
                                                    }}
                                                />
                                                <label className="form-check-label" htmlFor={`state-${stateReport.stateName}`}>
                                                    {stateReport.stateName}
                                                </label>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                                <div className="modal-footer">
                                    <button
                                        type="button"
                                        className="btn btn-secondary"
                                        onClick={() => setStateSelectionModal(false)}
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        type="button"
                                        className="btn btn-primary"
                                        onClick={() => {
                                            const selectedStates = Object.keys(selectedStatesForPrint).filter(s => selectedStatesForPrint[s]);
                                            if (selectedStates.length === 0) {
                                                alert("Please select at least one state");
                                                return;
                                            }
                                            generatePdf(selectedStates);
                                            setStateSelectionModal(false);
                                        }}
                                    >
                                        Print Selected
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </>
            )}
        </>
    );
};

export default DoctorRevenue;
